import { lazy, memo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import i18n from '~/common/helpers/i18n';
import { ConfirmDialogProvider } from '~/components/ConfirmDialogV2/contexts/confirmDialogProvider';
import { ToastProvider } from '~/components/ToastV2/ToastContext';
import { CanvasErrorBoundary } from '~/scenes/Build/scenes/FlowBuilder/components/CanvasErrorBoundary';
import HistoryPipeline from '~/scenes/Build/scenes/PipelineHistory';
import Operation from '~/scenes/Others/scenes/App/Operation';

const CapsuleDesign = lazy(
  () => import('~/scenes/Build/scenes/FlowBuilder/CapsuleDesign')
);
const Capsules = lazy(() => import('~/scenes/Build/scenes/Capsules'));
const PipelineDesign = lazy(
  () => import('~/scenes/Build/scenes/FlowBuilder/PipelineDesign')
);
const Pipelines = lazy(() => import('~/scenes/Build/scenes/Pipelines'));
const GlobalsBuild = lazy(() => import('~/scenes/Build/scenes/Globals'));
const BuildAccountsRoot = lazy(() =>
  import('~/scenes/Build/scenes/BuildAccounts').then(m => ({
    default: m.BuildAccountsRoot
  }))
);

const GlobalsBuildWithProvider = memo(() => (
  <ConfirmDialogProvider>
    <GlobalsBuild />
  </ConfirmDialogProvider>
));
GlobalsBuildWithProvider.displayName = 'GlobalsBuildWithProvider';

const VersionDiffViewer = lazy(
  () => import('~/scenes/Build/scenes/VersionDiffViewer')
);

const PipelineDesignWithProviders = memo(() => (
  <CanvasErrorBoundary entityType='pipeline'>
    <ConfirmDialogProvider>
      <ToastProvider>
        <PipelineDesign />
      </ToastProvider>
    </ConfirmDialogProvider>
  </CanvasErrorBoundary>
));
PipelineDesignWithProviders.displayName = 'PipelineDesignWithProviders';

const CapsuleDesignWithProviders = memo(() => (
  <CanvasErrorBoundary entityType='capsule'>
    <ConfirmDialogProvider>
      <ToastProvider>
        <CapsuleDesign />
      </ToastProvider>
    </ConfirmDialogProvider>
  </CanvasErrorBoundary>
));
CapsuleDesignWithProviders.displayName = 'CapsuleDesignWithProviders';

const PipelineHistoryWithBoundary = memo(() => (
  <CanvasErrorBoundary entityType='pipeline'>
    <PipelineDesign />
  </CanvasErrorBoundary>
));
PipelineHistoryWithBoundary.displayName = 'PipelineHistoryWithBoundary';

const CapsulesWithProvider = memo(() => (
  <ConfirmDialogProvider>
    <Capsules />
  </ConfirmDialogProvider>
));
CapsulesWithProvider.displayName = 'CapsulesWithProvider';

const PipelineVersionDiffViewerWithProvider = memo(() => (
  <ConfirmDialogProvider>
    <ToastProvider>
      <VersionDiffViewer type='pipeline' />
    </ToastProvider>
  </ConfirmDialogProvider>
));
PipelineVersionDiffViewerWithProvider.displayName =
  'PipelineVersionDiffViewerWithProvider';

const CapsuleVersionDiffViewerWithProvider = memo(() => (
  <ConfirmDialogProvider>
    <ToastProvider>
      <VersionDiffViewer type='capsule' />
    </ToastProvider>
  </ConfirmDialogProvider>
));
CapsuleVersionDiffViewerWithProvider.displayName =
  'CapsuleVersionDiffViewerWithProvider';

/**
 * Redirect old /:realm/design/version-diff-viewer/:pipelineName
 * to new  /:realm/design/version-diff-viewer/pipeline/:pipelineName
 * while preserving query params.
 */
export const LegacyVersionDiffRedirect = () => {
  const { realm = '', pipelineName = '' } = useParams<{
    realm: string;
    pipelineName: string;
  }>();
  const location = useLocation();
  return (
    <Navigate
      to={`/${encodeURIComponent(realm)}/design/version-diff-viewer/pipeline/${encodeURIComponent(pipelineName)}${location.search}`}
      replace
    />
  );
};

const TRANSLATIONS = {
  pipelinesTab: i18n.t('label.pipelines_tab'),
  capsulesTab: i18n.t('label.capsules_tab'),
  pipelineHistoryTab: i18n.t('label.pipeline_history_tab'),
  pipelinePlural: i18n.t('common.labels.pipeline_plural'),
  capsulePlural: i18n.t('scenes.beta.labels.capsule_plural'),
  versionDiffViewerTitle: i18n.t('label.version_diff_viewer_title'),
  globalsPlural: i18n.t('scenes.beta.labels.global_plural'),
  accountPlural: i18n.t('label.account_plural')
};

const BuildOperation = memo(() => {
  const links = [
    {
      roles: ['PIPELINE:READ'],
      'data-testid': 'build-pipeline-tab',
      routeName: 'build-pipelines',
      path: 'operation/build/pipelines',
      name: i18n.t('scenes.beta.labels.upper_and_capitalize.capitalized', {
        item: TRANSLATIONS.pipelinePlural
      })
    },
    {
      roles: ['CAPSULE:READ'],
      'data-testid': 'build-capsule-tab',
      routeName: 'build-capsules',
      path: 'operation/build/capsules',
      name: i18n.t('scenes.beta.labels.upper_and_capitalize.capitalized', {
        item: TRANSLATIONS.capsulePlural
      })
    },
    {
      roles: ['GLOBAL:READ'],
      'data-testid': 'build-globals-tab',
      routeName: 'build-globals',
      path: 'operation/build/globals',
      name: i18n.t('scenes.beta.labels.upper_and_capitalize.capitalized', {
        item: TRANSLATIONS.globalsPlural
      })
    },
    {
      roles: ['ACCOUNT:READ'],
      'data-testid': 'build-accounts-tab',
      routeName: 'build-accounts',
      path: 'operation/build/accounts',
      name: TRANSLATIONS.accountPlural ?? 'Accounts'
    }
  ];

  return <Operation links={links} />;
});
BuildOperation.displayName = 'BuildOperation';

const BUILD_ROUTES = [
  {
    path: '/:realm/design/version-diff-viewer/pipeline/:pipelineName',
    name: 'build-version-diff-viewer-pipeline',
    title: TRANSLATIONS.versionDiffViewerTitle,
    element: <PipelineVersionDiffViewerWithProvider />
  },
  {
    path: '/:realm/design/version-diff-viewer/capsule/:capsuleName',
    name: 'build-version-diff-viewer-capsule',
    title: TRANSLATIONS.versionDiffViewerTitle,
    element: <CapsuleVersionDiffViewerWithProvider />
  },
  {
    path: '/:realm/design/version-diff-viewer/:pipelineName',
    name: 'build-version-diff-viewer-legacy',
    title: TRANSLATIONS.versionDiffViewerTitle,
    element: <LegacyVersionDiffRedirect />
  },
  {
    path: '/:realm/design/pipelines/:pipelineId',
    name: 'build-design-pipeline',
    title: TRANSLATIONS.pipelinesTab,
    element: <PipelineDesignWithProviders />
  },
  {
    path: '/:realm/design/v2/pipelines/:pipelineId',
    name: 'build-design-pipeline-v2',
    title: TRANSLATIONS.pipelinesTab,
    element: <PipelineDesignWithProviders />
  },
  {
    path: '/:realm/design/capsules/:capsuleId',
    name: 'build-design-capsule',
    title: TRANSLATIONS.capsulesTab,
    element: <CapsuleDesignWithProviders />
  },
  {
    path: '/:realm/design/v2/capsules/:capsuleId',
    name: 'build-design-capsule-v2',
    title: TRANSLATIONS.capsulesTab,
    element: <CapsuleDesignWithProviders />
  },
  {
    path: '/:realm/design/pipelines/history/:pipelineId',
    name: 'build-pipeline-history',
    title: TRANSLATIONS.pipelineHistoryTab,
    element: <PipelineHistoryWithBoundary />
  },
  {
    path: '/:realm/operation/build',
    element: <BuildOperation />,
    children: [
      {
        path: 'pipelines',
        name: 'build-pipelines',
        title: TRANSLATIONS.pipelinesTab,
        element: <Pipelines />,
        children: [
          {
            path: ':name/:versionMajor/history',
            title: TRANSLATIONS.pipelineHistoryTab,
            element: <HistoryPipeline />
          }
        ]
      },
      {
        path: 'capsules',
        name: 'build-capsules',
        title: TRANSLATIONS.capsulesTab,
        element: <CapsulesWithProvider />
      },
      {
        path: 'globals',
        name: 'build-globals',
        title: TRANSLATIONS.globalsPlural,
        element: <GlobalsBuildWithProvider />
      },
      {
        path: 'accounts',
        name: 'build-accounts',
        title: i18n.t('label.account_plural'),
        element: <BuildAccountsRoot />
      },
      {
        path: 'oauth-providers',
        name: 'build-oauth-providers',
        title: i18n.t('label.oauth_providers'),
        element: <BuildAccountsRoot />
      }
    ]
  }
];

const BuildRoutes = () => BUILD_ROUTES;

export default BuildRoutes;
